import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { X, PencilSimple, Spinner, CheckCircle, Warning, ArrowRight } from '@phosphor-icons/react';

/**
 * ContactUpdatePreviewDrawer — opens when Crunchy's `update_contacts` tool fires.
 * Shows before→after changes per contact; "Apply" POSTs the approved batch to
 * /api/ai/contacts/bulk-update. Nothing is saved until the user approves here.
 * Notes are append-only on the backend — existing notes are never overwritten.
 */
export default function ContactUpdatePreviewDrawer({ open, onClose, preview }) {
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSaving(false);
    setResult(null);
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, preview, onClose]);

  const updates = preview?.updates || [];
  const issues = preview?.issues || [];

  const handleApply = async () => {
    if (saving || updates.length === 0) return;
    setSaving(true);
    setResult(null);
    try {
      const { data } = await api.post('/ai/contacts/bulk-update', { updates });
      setResult(data);
      const n = data?.updated_count || 0;
      toast.success(
        `✓ ${n} contact${n !== 1 ? 's' : ''} updated${data?.notes_logged ? ` · ${data.notes_logged} note${data.notes_logged !== 1 ? 's' : ''} added` : ''}${data?.batch_tag ? ` · tagged "${data.batch_tag}"` : ''}`,
        {
          duration: 10000,
          position: 'top-center',
          action: { label: 'View Contacts', onClick: () => { window.location.href = '/contacts'; } },
        },
      );
      if ((data?.errors || []).length === 0) {
        setTimeout(() => onClose?.(), 2500);
      }
    } catch (err) {
      setResult({ error: err.response?.data?.detail || err.message || 'Update failed' });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex justify-end" data-testid="contact-update-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={saving ? undefined : onClose} />

      <div className="relative w-full max-w-[640px] h-full bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 bg-gradient-to-b from-[#0B5394] to-[#073763] text-white flex items-center gap-2">
          <PencilSimple size={18} weight="bold" className="text-[#F39C12]" />
          <div className="flex-1">
            <div className="font-bold text-sm">Review contact updates</div>
            <div className="text-[10px] text-white/70">
              {updates.length} contact{updates.length !== 1 ? 's' : ''} matched — nothing is saved until you approve. Notes are added, never overwritten.
            </div>
          </div>
          <button onClick={onClose} disabled={saving} className="text-white/80 hover:text-white p-1" data-testid="contact-update-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Updates list */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {updates.length === 0 && (
            <div className="px-6 py-8 text-center" data-testid="contact-update-empty">
              <Warning size={22} weight="fill" className="text-amber-500 mx-auto mb-2" />
              <div className="text-xs font-bold text-slate-700 mb-1">No contacts matched in this workspace</div>
              <div className="text-[11px] text-slate-500 leading-relaxed max-w-[380px] mx-auto">
                Nothing will be updated. Check the list below for why each one was skipped —
                and make sure you&apos;re logged into the account that actually holds these contacts.
              </div>
            </div>
          )}
          {updates.map((u, i) => (
            <div key={i} className="px-4 py-3" data-testid="contact-update-row">
              <div className="font-bold text-xs text-slate-900">
                {u.name || 'Unnamed'}
                {u.company && <span className="font-normal text-slate-500"> · {u.company}</span>}
              </div>
              {(u.changes || []).length > 0 && (
                <div className="mt-1.5 space-y-1">
                  {u.changes.map((ch, j) => (
                    <div key={j} className="flex items-center gap-1.5 text-[11px]">
                      <span className="text-slate-500 w-24 shrink-0">{ch.label}:</span>
                      {ch.old ? <span className="text-red-600 line-through truncate max-w-[130px]">{ch.old}</span> : <span className="text-slate-300">empty</span>}
                      <ArrowRight size={10} className="text-slate-400 shrink-0" />
                      <span className="text-emerald-700 font-semibold truncate">{ch.new}</span>
                    </div>
                  ))}
                </div>
              )}
              {(u.add_tags || []).length > 0 && (
                <div className="mt-1.5 text-[11px]">
                  <span className="text-slate-500">Add tags: </span>
                  {u.add_tags.map((t) => (
                    <span key={t} className="inline-block bg-blue-50 text-blue-800 rounded px-1.5 py-0.5 mr-1 text-[10px] font-semibold">{t}</span>
                  ))}
                </div>
              )}
              {u.append_note && (
                <div className="mt-1.5 text-[11px] bg-amber-50/60 border border-amber-100 rounded px-2.5 py-1.5">
                  <div className="text-[9px] font-bold uppercase tracking-wide text-amber-700 mb-0.5">New note (added on top of existing notes)</div>
                  <div className="text-slate-700 whitespace-pre-wrap max-h-24 overflow-y-auto">{u.append_note}</div>
                </div>
              )}
            </div>
          ))}
          {issues.length > 0 && (
            <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-100" data-testid="contact-update-issues">
              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-1">
                {issues.length} need{issues.length === 1 ? 's' : ''} attention (won't be updated)
              </div>
              {issues.map((iss, i) => (
                <div key={i} className="text-[11px] text-amber-800">
                  <strong>{iss.match || '—'}</strong>: {iss.problem}
                  {(iss.candidates || []).length > 0 && <span className="text-amber-600"> ({iss.candidates.join(' / ')})</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 py-3 space-y-2">
          {result && !result.error && (
            <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="contact-update-success">
              <CheckCircle size={14} weight="fill" />
              Updated {result.updated_count} contact{result.updated_count !== 1 ? 's' : ''}
              {(result.errors || []).length > 0 && ` · ${result.errors.length} failed`}
            </div>
          )}
          {result?.error && (
            <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="contact-update-error">
              <Warning size={14} weight="fill" /> {result.error}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={onClose}
              disabled={saving}
              className="text-xs text-slate-600 hover:text-slate-900 px-4 py-2"
              data-testid="contact-update-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={saving || updates.length === 0 || (result && !result.error)}
              className="bg-[#F39C12] hover:bg-[#E08C00] disabled:opacity-50 text-black font-bold text-xs rounded px-5 py-2 flex items-center gap-1.5"
              data-testid="contact-update-apply-btn"
            >
              {saving ? (<><Spinner size={13} className="animate-spin" /> Applying…</>)
                : (<><PencilSimple size={13} weight="bold" /> Apply {updates.length} update{updates.length !== 1 ? 's' : ''}</>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
